import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Divider, 
  Paper, 
  Typography,
  useTheme
} from '@mui/material';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { motion } from 'framer-motion';

import { getScanStats } from '../../services/api';

// Add domain colors for visualization
const getDomainColor = (domain) => {
  const colorMap = {
    'youtube.com': '#FF0000',
    'instagram.com': '#E1306C',
    'twitter.com': '#1DA1F2',
    'facebook.com': '#4267B2',
    'reddit.com': '#FF4500',
    'tiktok.com': '#000000',
    'pinterest.com': '#E60023'
  };
  
  return colorMap[domain] || '#' + Math.floor(Math.random()*16777215).toString(16);
};

const SimpleAnalyticsView = ({ creatorName, scanResults }) => {
  const theme = useTheme();
  const [analyticsData, setAnalyticsData] = useState(scanResults?.stats || {
    domainDistribution: [],
    contentTypeDistribution: [],
    confidenceDistribution: [],
    discoveryTimeline: [],
    contentAgeDistribution: []
  });
  const [isLoading, setIsLoading] = useState(!scanResults?.stats);
  const [error, setError] = useState(null);
  
  // Fetch scan statistics if not provided
  useEffect(() => {
    if (!scanResults?.stats && creatorName) {
      const fetchStats = async () => {
        try {
          setIsLoading(true);
          setError(null);
          const data = await getScanStats(creatorName);
          
          // Add colors to domain distribution
          if (data.domainDistribution) {
            data.domainDistribution = data.domainDistribution.map(item => ({
              ...item,
              color: getDomainColor(item.id)
            }));
          }
          
          // Add colors to content type distribution
          if (data.contentTypeDistribution) {
            data.contentTypeDistribution = data.contentTypeDistribution.map((item, index) => ({
              ...item,
              color: ['#FF6B6B', '#4ECDC4', '#1A535C'][index % 3]
            }));
          }
          
          // Add colors to confidence distribution
          if (data.confidenceDistribution) {
            data.confidenceDistribution = data.confidenceDistribution.map((item, index) => ({
              ...item,
              color: ['#FF6B6B', '#FFE66D', '#4ECDC4'][index % 3]
            }));
          }
          
          setAnalyticsData(data);
        } catch (err) {
          console.error('Error fetching scan statistics:', err);
          setError('Failed to load analytics data. Please try again.');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchStats();
    }
  }, [creatorName, scanResults]);
  
  return (
    <Box sx={{ width: '100%' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
          Analytics for {creatorName || 'Example Creator'}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Discovery Timeline */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Discovery Timeline</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              When leaked content was discovered over time
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveLine
                data={analyticsData.discoveryTimeline}
                margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 'auto' }}
                curve="cardinal"
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: 'Date',
                  legendOffset: 40,
                  legendPosition: 'middle',
                  format: value => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Count',
                  legendOffset: -40,
                  legendPosition: 'middle'
                }}
                colors={{ scheme: 'category10' }}
                pointSize={10}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 0,
                    itemsSpacing: 0,
                    itemDirection: 'left-to-right',
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)'
                  }
                ]}
                theme={{
                  axis: {
                    ticks: {
                      text: {
                        fill: theme.palette.text.secondary
                      }
                    },
                    legend: {
                      text: {
                        fill: theme.palette.text.primary
                      }
                    }
                  },
                  grid: {
                    line: {
                      stroke: theme.palette.divider
                    }
                  }
                }}
              />
            </Box>
          </Paper>
          
          {/* Distribution Charts Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Domain Distribution */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, flex: '1 1 30%', minWidth: '300px' }}>
              <Typography variant="h6" gutterBottom>Domain Distribution</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Where leaked content was found
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsivePie
                  data={analyticsData.domainDistribution}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  colors={{ datum: 'data.color' }}
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor={theme.palette.text.primary}
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                  legends={[
                    {
                      anchor: 'bottom',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: theme.palette.text.secondary,
                      itemDirection: 'left-to-right',
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: 'circle'
                    }
                  ]}
                />
              </Box>
            </Paper>
            
            {/* Content Type Distribution */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, flex: '1 1 30%', minWidth: '300px' }}>
              <Typography variant="h6" gutterBottom>Content Type Distribution</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Types of leaked content
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsivePie
                  data={analyticsData.contentTypeDistribution}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  colors={{ datum: 'data.color' }}
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor={theme.palette.text.primary}
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                  legends={[
                    {
                      anchor: 'bottom',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: theme.palette.text.secondary,
                      itemDirection: 'left-to-right',
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: 'circle'
                    }
                  ]}
                />
              </Box>
            </Paper>
            
            {/* Confidence Distribution */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, flex: '1 1 30%', minWidth: '300px' }}>
              <Typography variant="h6" gutterBottom>Confidence Distribution</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Confidence levels of content matches
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsivePie
                  data={analyticsData.confidenceDistribution}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  colors={{ datum: 'data.color' }}
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor={theme.palette.text.primary}
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                  legends={[
                    {
                      anchor: 'bottom',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: theme.palette.text.secondary,
                      itemDirection: 'left-to-right',
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: 'circle'
                    }
                  ]}
                />
              </Box>
            </Paper>
          </Box>
          
          {/* Content Age Distribution */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Content Age Distribution</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Age of leaked content when discovered
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveBar
                data={analyticsData.contentAgeDistribution}
                keys={['count']}
                indexBy="month"
                margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={{ scheme: 'nivo' }}
                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Month',
                  legendPosition: 'middle',
                  legendOffset: 32
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Count',
                  legendPosition: 'middle',
                  legendOffset: -40
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 120,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 100,
                    itemHeight: 20,
                    itemDirection: 'left-to-right',
                    itemOpacity: 0.85,
                    symbolSize: 20
                  }
                ]}
                theme={{
                  axis: {
                    ticks: {
                      text: {
                        fill: theme.palette.text.secondary
                      }
                    },
                    legend: {
                      text: {
                        fill: theme.palette.text.primary
                      }
                    }
                  },
                  grid: {
                    line: {
                      stroke: theme.palette.divider
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </Box>
      </motion.div>
    </Box>
  );
};

export default SimpleAnalyticsView;
